import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../platform/firebase/clients';
import { AiDetectionMetric } from '../domain/aiDetectionMetrics';
import type { AiTextDetectionResult } from '../domain/aiTextDetection';

export const AI_DETECTION_COLLECTION = 'ai_detection';

function toDateOrNull(value: unknown): Date | null {
  if (value instanceof Timestamp) return value.toDate();
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (value instanceof Date) return value;
  return null;
}

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function counterOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

export function mapAiDetectionMetric(
  _id: string,
  data: Record<string, unknown>,
): AiDetectionMetric | null {
  const expertID = stringOrNull(data.expertID);
  if (!expertID) return null;

  return {
    expertID,
    name: stringOrNull(data.name) ?? expertID,
    email: stringOrNull(data.email),
    detectionRuns: counterOrZero(data.detectionRuns),
    infoFlags: counterOrZero(data.infoFlags),
    warningFlags: counterOrZero(data.warningFlags),
    lastRunAt: toDateOrNull(data.lastRunAt),
  };
}

/** Read the server-maintained per-expert AI-detection aggregates for admins. */
export async function fetchAiDetectionMetrics(): Promise<AiDetectionMetric[]> {
  const snapshot = await getDocs(collection(db, AI_DETECTION_COLLECTION));
  return snapshot.docs
    .map((document) => mapAiDetectionMetric(document.id, document.data() as Record<string, unknown>))
    .filter((metric): metric is AiDetectionMetric => metric !== null);
}

// Pangram polling gives up after 45s server-side; leave slack for cold starts.
const DETECT_TIMEOUT_MS = 2 * 60 * 1000;

/** Score pasted text with Pangram (admin only). Nothing is stored. */
export async function detectAiTextForAdmin(text: string): Promise<AiTextDetectionResult> {
  const callable = httpsCallable<{ text: string }, AiTextDetectionResult>(
    functions,
    'detectAiTextForAdmin',
    { timeout: DETECT_TIMEOUT_MS },
  );
  const response = await callable({ text });
  return response.data;
}
